// The structure of the form is usually not equal to the logical structure of the
// data as provided by FIM.
// Therefore, the validator is used to validate all compound constraints (meaning, all constratints which
// must be checked by looking at two or more inputs).
//
// TODO: Helptext are currently also supported via a HelpTextValidator, which emulates an always valid and empty input field.
// As a result, the field is also included in JSON output.
// Unfortunately, the field cannot be completely ignored, as AFAIK a help text could still be the target of a rule (e.g. to only conditionally show a info text).

import { assert } from "../project";
import type {
  ChildConfig,
  FieldConfig,
  GroupConfig,
  HelpTextConfig,
  RuleConfig,
  RuleExpression,
  RuleOperation,
  SchemaConfig,
} from "./config/config";
import {
  Form,
  type HelpText,
  type Input,
  type Page,
  type Section,
  ValidationState,
} from "./ui";

export class SchemaValidator {
  private children: { [key: string]: ElementValidator };
  private childStates: Record<string, ValidationState>;

  private form: Form;
  private rules: RuleConfig[];

  constructor(private config: SchemaConfig) {
    this.rules = config.rules;

    this.form = new Form(() => {
      const data: Record<string, unknown> = {};
      for (const key of Object.keys(this.children)) {
        data[key] = this.children[key].getValue();
      }
    });

    this.children = {};
    this.childStates = {};
    for (let i = 0; i < config.children.length; i++) {
      const childConfig = config.children[i];
      const pageValidator = new PageValidator(childConfig, this.form, (state) =>
        this.onChildUpdate(childConfig.config.id, state),
      );

      this.children[childConfig.config.id] = pageValidator;
    }

    this.evaluateRules();
    for (const childId in this.children) {
      this.childStates[childId] = this.children[childId].validate();
    }

    this.form.finish();
    this.form.update(this.validate(), this.getValue());
  }

  private onChildUpdate(childId: string, state: ValidationState) {
    this.childStates[childId] = state;
    this.evaluateRules();

    this.form.update(this.validate(), this.getValue());
  }

  // We assume for now, that the top-level schema is always an AND group.
  public validate(): ValidationState {
    for (const childState of Object.values(this.childStates)) {
      if (childState === ValidationState.INVALID) {
        return ValidationState.INVALID;
      }
    }

    return ValidationState.VALID;
  }

  public getRoot(): HTMLElement {
    return this.form.root;
  }

  private evaluateRules() {
    for (const rule of this.rules) {
      console.assert(rule.type === "enable");
      const result = evaluateBoolExpression(this, rule.expression);
      const target = resolveChild(this, rule.targetId);
      target.setEnabled(result);
      this.childStates[rule.targetId] = target.validate();
    }
  }

  public getElementId(): string {
    return this.config.id;
  }

  public getChild(id: string): ElementValidator {
    return this.children[id];
  }

  public setEnabled(_isEnabled: boolean) {
    throw Error("Cannot disable/enable schema");
  }

  public getValue(): Record<string, unknown> | undefined {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(this.children)) {
      result[key] = this.children[key].getValue();
    }

    return result;
  }
}

export class PageValidator {
  elementId: string;

  private validator: ElementValidator;

  page: Page;

  constructor(
    config: ChildConfig,
    form: Form,
    onUpdate: (state: ValidationState) => void,
  ) {
    this.elementId = config.config.id;

    if (config.type === "field") {
      this.page = form.createPage(config.config.label);
      this.validator = new FieldValidator(
        config.config,
        (state) => {
          this.page.setState(state);
          onUpdate(state);
        },
        this.page,
      );
    } else if (config.type === "help") {
      this.page = form.createPage(config.config.label);
      this.validator = new HelpTextValidator(config.config, this.page);
    } else {
      console.assert(config.type === "group");

      this.page = form.createPage(config.config.title);
      this.validator = new GroupValidator(
        config.config,
        (state) => {
          this.page.setState(state);
          onUpdate(state);
        },
        this.page,
      );
    }
  }

  public setEnabled(isEnabled: boolean) {
    this.validator.setEnabled(isEnabled);
  }

  public getValue(): Record<string, any> | string | undefined {
    return this.validator.getValue();
  }

  public validate(): ValidationState {
    const state = this.validator.validate();
    this.page.setState(state);

    return state;
  }

  public getElementId(): string {
    return this.elementId;
  }

  public getChild(id: string): ElementValidator {
    return this.validator.getChild(id);
  }
}

type Validator =
  | SchemaValidator
  | PageValidator
  | GroupValidator
  | FieldValidator
  | HelpTextValidator;

type ElementValidator =
  | PageValidator
  | GroupValidator
  | FieldValidator
  | HelpTextValidator;

export class GroupValidator {
  public children: Record<string, ElementValidator>;
  private childStates: Record<string, ValidationState>;
  private rules: RuleConfig[];
  private container: Page | Section;
  private isEnabled = true;

  constructor(
    private config: GroupConfig,
    private onUpdate: (state: ValidationState) => void,
    container: Page | Section,
  ) {
    this.container = container;
    this.rules = config.rules;

    this.children = {};
    this.childStates = {};
    for (const childConfig of config.children) {
      const childId = childConfig.config.id;

      if (childId in this.children) {
        throw Error(`Duplicate child-id ${childConfig.config.id}`);
      }

      if (childConfig.type === "group") {
        const groupConfig = childConfig.config;
        this.children[childId] = new GroupValidator(
          groupConfig,
          (state) => this.updateState(groupConfig.id, state),
          this.container.createSubSection(groupConfig.title),
        );
      } else if (childConfig.type === "field") {
        const fieldConfig = childConfig.config;
        this.children[childId] = new FieldValidator(
          fieldConfig,
          (state) => this.updateState(fieldConfig.id, state),
          this.container,
        );
      } else {
        console.assert(childConfig.type === "help");
        const helpConfig = childConfig.config;
        this.children[childId] = new HelpTextValidator(
          helpConfig,
          this.container,
        );
      }
    }

    this.evaluateRules();
    for (const childId in this.children) {
      this.childStates[childId] = this.children[childId].validate();
    }
  }

  public setEnabled(isEnabled: boolean) {
    this.isEnabled = isEnabled;
    this.container.setEnabled(isEnabled);
  }

  public getElementId(): string {
    return this.config.id;
  }

  public getChild(id: string): ElementValidator {
    return this.children[id];
  }

  public validate(): ValidationState {
    if (!this.isEnabled) {
      return ValidationState.VALID;
    }

    for (const childState of Object.values(this.childStates)) {
      if (childState === ValidationState.INVALID) {
        return ValidationState.INVALID;
      }
    }

    return ValidationState.VALID;
  }

  public updateState(id: string, state: ValidationState) {
    this.childStates[id] = state;
    this.evaluateRules();

    this.onUpdate(this.validate());
  }

  private evaluateRules() {
    for (const rule of this.rules) {
      console.assert(rule.type === "enable");
      const result = evaluateBoolExpression(this, rule.expression);
      const target = resolveChild(this, rule.targetId);
      target.setEnabled(result);
      this.childStates[rule.targetId] = target.validate();
    }
  }

  public getValue(): Record<string, unknown> | undefined {
    if (!this.isEnabled) {
      return undefined;
    }

    const result: Record<string, unknown> = {};
    for (const key of Object.keys(this.children)) {
      result[key] = this.children[key].getValue();
    }

    return result;
  }
}

export class FieldValidator {
  private input: Input;
  private isEnabled = true;

  constructor(
    private config: FieldConfig,
    onUpdate: (state: ValidationState) => void,
    container: Page | Section,
  ) {
    this.input = container.createInput(config, (state) => onUpdate(state));
  }

  public setEnabled(isEnabled: boolean) {
    this.isEnabled = isEnabled;
    this.input.setEnabled(isEnabled);
  }

  public getValue(): string | undefined {
    if (!this.isEnabled) {
      return undefined;
    }

    return this.input.getValue();
  }

  public validate(): ValidationState {
    if (!this.isEnabled) {
      return ValidationState.VALID;
    }

    return this.input.validate();
  }

  public getElementId(): string {
    return this.config.id;
  }

  public getChild(): ElementValidator {
    throw Error("Field has no children");
  }
}

export class HelpTextValidator {
  private helpText: HelpText;

  constructor(
    private config: HelpTextConfig,
    container: Page | Section,
  ) {
    this.helpText = container.createHelpText(config);
  }

  public setEnabled(isEnabled: boolean) {
    this.helpText.setEnabled(isEnabled);
  }

  public getValue(): undefined {
    return undefined;
  }

  public validate(): ValidationState {
    return ValidationState.VALID;
  }

  public getElementId(): string {
    return this.config.id;
  }

  public getChild(): ElementValidator {
    throw Error("Helptext has no children");
  }
}

function evaluateBoolExpression(
  scope: Validator,
  expression: RuleExpression,
): boolean {
  const result = evaluateExpression(scope, expression);
  if (typeof result === "boolean") {
    return result;
  }

  throw Error(`Expected boolean expression, got ${result}`);
}

function evaluateExpression(
  scope: Validator,
  expression: RuleExpression,
): unknown {
  if (typeof expression === "string") {
    return expression;
  } else if (typeof expression === "boolean") {
    return expression;
  }

  switch (expression.type) {
    case "identifier":
      return resolveChild(scope, expression.identifier).getValue();
    case "operation":
      return evaluateOperation(scope, expression);
    default:
      throw Error(`Unknown expression ${expression}`);
  }
}

function evaluateOperation(
  scope: Validator,
  operation: RuleOperation,
): boolean {
  const { operation: op, operands } = operation;

  switch (op) {
    case "AND":
      return operands.every((operand) => evaluateExpression(scope, operand));
    case "OR":
      return operands.some((operand) => evaluateExpression(scope, operand));
    case "==":
      return (
        evaluateExpression(scope, operands[0]) ===
        evaluateExpression(scope, operands[1])
      );
    default:
      throw Error(`Unknown operation ${op}`);
  }
}

function resolveChild(scope: Validator, identifier: string): Validator {
  const path = identifier.split(".");

  if (path.length === 0) {
    throw Error("Empty identifier");
  }

  const rootElementId = path.shift()!;
  if (rootElementId !== scope.getElementId()) {
    throw Error(
      `Expected root element ${scope.getElementId()}, got ${rootElementId}`,
    );
  }

  while (path.length > 0) {
    if (scope instanceof FieldValidator || scope instanceof HelpTextValidator) {
      throw Error(`Expected group or schema, got ${scope.getElementId()}`);
    }

    scope = scope.getChild(path.shift()!);
  }

  assert(path.length === 0);
  return scope;
}
